import { z } from 'zod'
import { polishRootSpawns, polishRootsFor, resolveGuidanceRoot } from '../../../../../src/guidance/roots.ts'
import type { FixMismatchReviewSchema } from '../../../../../src/lib/bug-management/fixMismatchReview.ts'

export const PullRequestFixSchema = z.object({
  number: z.number().int().positive(),
  body: z.string().nullable(),
  title: z.string(),
  state: z.enum(['open', 'closed']),
  draft: z.boolean().optional().default(false),
  merged: z.boolean(),
  merged_at: z.string().nullable(),
  merge_commit_sha: z.string().nullable(),
  head: z.object({ sha: z.string(), ref: z.string(), repo: z.object({ full_name: z.string() }).nullable().optional() }),
  base: z.object({ ref: z.string() }),
})
export type PullRequestFix = z.infer<typeof PullRequestFixSchema>

export interface FixReference { bugId?: string; issueRepo?: string; issueNumber?: number }

/** Only explicit fix declarations establish a relationship; quoted reports and code are not declarations. */
export function parseFixReferences(body: string | null, repo: string, qaSiteUrl = 'https://qa.replay.io'): FixReference[] {
  const text = (body ?? '').replace(/<!--[\s\S]*?-->/g, '').replace(/```[\s\S]*?```|~~~[\s\S]*?~~~/g, '')
    .replace(/^\s*>.*$/gm, '').replace(/`[^`\n]*`/g, '')
  const refs: FixReference[] = []
  const pattern = /\b(?:close[sd]?|fix(?:es|ed)?|resolve[sd]?)\s*:?\s+(?:\[[^\]\n]*\]\()?<?(https?:\/\/[^\s)>,]+|bug-[\w-]+|(?:[\w.-]+\/[\w.-]+)?#\d+)/gi
  for (const match of text.matchAll(pattern)) {
    const value = match[1]!.replace(/[.,;]+$/, '')
    if (/^bug-[\w-]+$/i.test(value)) { refs.push({ bugId: value }); continue }
    if (/^https?:/i.test(value)) {
      if (!URL.canParse(value)) continue
      const url = new URL(value)
      const bug = url.pathname.match(/^\/(?:projects|p)\/[^/]+\/bugs\/(bug-[\w-]+)\/?$/)
      if (bug && (url.origin === new URL(qaSiteUrl).origin || url.origin === 'https://qa.replay.io')) { refs.push({ bugId: bug[1]! }); continue }
      const issue = url.hostname === 'github.com' && url.pathname.match(/^\/([^/]+\/[^/]+)\/issues\/(\d+)\/?$/)
      if (issue) refs.push({ issueRepo: issue[1]!.toLowerCase(), issueNumber: Number(issue[2]) })
      continue
    }
    const issue = value.match(/^(?:([\w.-]+\/[\w.-]+))?#(\d+)$/)
    if (issue) refs.push({ issueRepo: (issue[1] ?? repo).toLowerCase(), issueNumber: Number(issue[2]) })
  }
  return refs
}

export const BugFixResultSchema = z.object({
  bug_id: z.string(), title: z.string(), project_id: z.string(),
  journey_id: z.string().nullable(), journey_version_id: z.string().nullable(),
  test_run_id: z.string().nullable(), replay_recording_id: z.string().nullable(),
  result: z.enum(['waiting', 'running', 'passed', 'reproduced', 'not-verified', 'not-tested']),
  detail: z.string(),
})
export type BugFixResult = z.infer<typeof BugFixResultSchema>

/** A required fix is eligible to pass only after its verification finishes successfully. */
export function bugVerificationState(results: Pick<BugFixResult, 'result'>[]): 'pending' | 'failed' | 'passed' {
  if (results.some(row => row.result === 'waiting' || row.result === 'running')) return 'pending'
  return results.some(row => row.result !== 'passed' && row.result !== 'not-tested') ? 'failed' : 'passed'
}

const PolishEvidenceSchema = z.object({
  nominations: z.array(z.object({ signal: z.string() })),
  diagnostics: z.object({
    inputs: z.record(z.number().nonnegative()).optional(),
    errors: z.array(z.string()).optional(),
    droppedAtCap: z.array(z.unknown()).optional(),
  }),
})
const RetestSetupSchema = z.object({ polish_pass1: z.record(z.unknown()).optional() })
export const VerificationTaskSchema = z.object({
  id: z.string(), status: z.string(), followup_script: z.string().nullable(),
})
type VerificationTask = z.infer<typeof VerificationTaskSchema>

/** No nominations is evidence only when the pass read recording data and completed its analysis. */
export function polishVerificationGap(category: string | null, setup: unknown, tasks: VerificationTask[]): string | null {
  if (tasks.some(task => task.followup_script && task.status !== 'completed')) {
    return 'Recording analysis or bug-filing followups did not complete'
  }
  if (!category) return null
  const parsed = RetestSetupSchema.safeParse(setup)
  const pass = PolishEvidenceSchema.safeParse(parsed.success ? parsed.data.polish_pass1?.[category] : undefined)
  if (!pass.success) return `The ${category} polish pass did not record a completed analysis`
  const { nominations, diagnostics } = pass.data
  if (diagnostics.errors?.length) return `The ${category} polish analysis encountered an error`
  if (!Object.values(diagnostics.inputs ?? {}).some(count => count > 0)) {
    return `The ${category} polish pass has no recorded input coverage`
  }
  if (diagnostics.droppedAtCap?.length) return `The ${category} polish analysis left candidates unchecked`
  if (nominations.length) {
    const roots = polishRootsFor(category).filter(root => polishRootSpawns(category, root, nominations))
    if (!roots.length || roots.some(root => !tasks.some(task => resolveGuidanceRoot(task.followup_script) === root && task.status === 'completed'))) {
      return `The ${category} polish followups did not complete`
    }
  }
  return null
}

type MismatchReview = z.infer<typeof FixMismatchReviewSchema>
const ACTIVE_TASK_STATUSES = ['queued', 'running', 'awaiting-deploy']
export const INCONCLUSIVE_MISMATCH_REVIEW = { sufficient: false, inconclusive: true, reason: 'Bug-specific review ended without a saved verdict' }

/** A review that ended without a verdict is inconclusive; only an unreviewed, recorded, otherwise-complete session mismatch enqueues one. */
export function mismatchReviewDecision(input: {
  reviewMismatches: boolean; mismatchReview: MismatchReview | null; reviewTaskStatus: string | undefined;
  pending: boolean; unavailable: string | null; reproduced: boolean; runStatus: string | null; outcome: string | null;
  outcomeReason: string | null; recording: string | null; analysisGap: string | null;
}): { enqueue: boolean; mismatchReview: MismatchReview | null } {
  const ended = input.reviewTaskStatus !== undefined && !ACTIVE_TASK_STATUSES.includes(input.reviewTaskStatus)
  const mismatchReview = input.mismatchReview ?? (ended ? { ...INCONCLUSIVE_MISMATCH_REVIEW } : null)
  const enqueue = input.reviewMismatches && !mismatchReview && !input.pending && !input.unavailable && !input.reproduced
    && input.runStatus === 'failed' && input.outcome === 'failed' && input.outcomeReason === 'user-session-mismatch'
    && !!input.recording && !input.analysisGap
  return { enqueue, mismatchReview }
}

export function classifyBugFixResult(input: {
  unavailable: string | null; runStatus: string | null; outcome: string | null;
  reproduced: boolean; recording: string | null; pending: boolean;
  analysisGap?: string | null; polishCategory?: string | null;
  outcomeReason?: string | null; mismatchReview?: { sufficient: boolean; reason: string } | null;
}): Pick<BugFixResult, 'result' | 'detail'> {
  if (input.unavailable) return { result: 'not-verified', detail: input.unavailable }
  if (!input.runStatus) return { result: 'waiting', detail: 'Waiting for the PR preview and a reproduction run' }
  if (input.pending || input.runStatus === 'in-progress') return { result: 'running', detail: 'Retesting the original journey' }
  if (input.reproduced) return { result: 'reproduced', detail: 'The linked bug was reported again' }
  if (input.runStatus === 'failed' && input.outcome === 'failed' && input.outcomeReason === 'user-session-mismatch'
    && input.recording && !input.analysisGap && input.mismatchReview) {
    return { result: input.mismatchReview.sufficient ? 'passed' : 'not-verified', detail: input.mismatchReview.reason }
  }
  if (input.runStatus === 'completed' && input.outcome === 'passed' && input.recording) {
    if (input.analysisGap) return { result: 'not-verified', detail: input.analysisGap }
    return { result: 'passed', detail: input.polishCategory
      ? `Original bug circumstances retested on this revision; ${input.polishCategory} polish analysis completed without detecting the linked bug again`
      : 'Original reproduction journey and its checks passed on this revision; the linked bug was not detected again' }
  }
  return { result: 'not-verified', detail: `Reproduction not verified (${input.outcome ?? input.runStatus})` }
}
